import axios from "./axios";
import { AppEnvironment } from "../components/common/reportContext";
import { FailedRequestRecord } from "../components/common/lastFailedRequest";
import { ReportProblemSource } from "../components/common/reportProblemBus";

/**
 * POST /feedback — the Report a problem submission (AKL-798).
 *
 * Always multipart, with and without a screenshot, so one request shape covers both. The
 * wire contract is frozen jointly with the API plan (`AKL-798-feedback-endpoint`) and cannot
 * be verified from this repo, so neither side may rename a field without changing both.
 *
 * **No email field.** The dialog shows the signed-in user their own address so they know who
 * the report will be attributed to, but the address is deliberately *not* sent: the server
 * takes the identity from the request token, so a report can never be attributed to the
 * wrong person. Do not "fix" the web to start sending it, and do not add a field for it on
 * the API side.
 *
 * **This module normalises its own errors.** It is the one request in the app that sets
 * `skipGlobalErrorHandler`, because that is the only flag suppressing both the
 * `/unexpectedError` redirect (which would throw away the text the user just typed) and any
 * competing toast, and it also stops a 401 on the feedback call from clearing the user's
 * token. The documented cost of that flag (`docs/ARCHITECTURE_RECOMMENDATIONS.md`) is that it
 * bypasses *all* interceptor special-casing — the 401 token-clear, 4xx normalisation and the
 * network/timeout toasts — which is why the narrower `suppressUnexpectedErrorRedirect` was
 * added for AKL-706. The consequence that matters here is concrete: the interceptor returns
 * early, so the rejection arrives as a raw axios error rather than the `{message, status,
 * data}` shape every other caller sees. Hence the defensive reads below and the small
 * discriminated result, so the dialog renders the right sentence without knowing anything
 * about axios.
 */

/**
 * The status the API uses to refuse an hourly-limit-exceeded report.
 *
 * Frozen with the API contract (see the AKL-798 plans) but **not verifiable from this repo** —
 * the endpoint lives in `akili.admin.api`. Any other 4xx is treated as the generic failure,
 * so correcting this is a one-line change.
 */
export const RATE_LIMIT_STATUS = 429;

/** What the dialog needs to know about the outcome. Nothing axios-shaped escapes this module. */
export type FeedbackResult =
  | { outcome: "sent"; guid: string | null }
  /** The API refused it: too many reports in the last hour. */
  | { outcome: "rateLimited" }
  /** There is no token to send with, so nothing was attempted. */
  | { outcome: "signedOut" }
  /** Anything else — a 4xx we do not have specific copy for, a 5xx, or no response at all. */
  | { outcome: "failed" };

/** Everything one report carries. Assembled by the dialog; field names are mapped below. */
export type FeedbackReportInput = {
  /** Required. What the user typed in "What happened?". */
  whatHappened: string;
  /** Optional. What they typed in "What did you expect to happen?". */
  expected: string;
  /** Which entry point the report was started from. */
  source: ReportProblemSource;
  /** The path the user was on, query string stripped. */
  page: string;
  browser: string;
  userAgent: string;
  appEnv: AppEnvironment;
  /** The API host — the `api_base_url` field carries the host, not the whole address. */
  apiHost: string;
  /** ISO-8601 UTC, captured when the dialog opened. */
  clientTime: string;
  /** The last failed request the app saw, where there was one. */
  failedRequest: FailedRequestRecord | null;
  /** The App Insights session id where one exists. Best effort — empty is normal. */
  aiSessionId: string;
  /** The attached screenshot, where there is one. */
  screenshot: File | null;
};

/** The 202 body. Only `guid` is used by the web. */
type FeedbackResponseDto = {
  guid?: string;
  email_status?: string;
  slack_status?: string;
  created_at?: string;
};

/**
 * The bits of a raw axios rejection this module reads. Declared locally rather than importing
 * `AxiosError`: the `axios` package is ESM and the test suite mocks `./axios`, so nothing here
 * should pull the real package into the module graph.
 */
type RawAxiosRejection = {
  response?: { status?: number; data?: { detail?: string } };
  code?: string;
};

const statusOf = (err: unknown): number | undefined =>
  typeof err === "object" && err !== null
    ? (err as RawAxiosRejection).response?.status
    : undefined;

/**
 * Send one problem report.
 *
 * Reads `localStorage("token")` directly instead of going through `authHeader()`, which is
 * not a pure getter: with no token it calls `localStorage.clear()` and triggers a Keycloak
 * logout redirect. Cheap insurance against signing a user out of the page they are trying to
 * report from. (Defensive, not a fix for an observed flow — the interceptor's 401 branch
 * throws `UnauthorizedError` and `ProfileProvider` routes to `/unauthorized`, and any hard
 * navigation re-initialises Keycloak with `onLoad: "login-required"`, so the token is
 * normally re-stored.) This is a service function, not a component body, so the read does
 * not breach the no-side-effects-during-render rule.
 * @param input what the user typed plus the context captured when the dialog opened
 * @returns the outcome the dialog should render; never throws for an HTTP failure
 */
export async function sendFeedbackReport(
  input: FeedbackReportInput,
): Promise<FeedbackResult> {
  if (!localStorage.getItem("token")) {
    return { outcome: "signedOut" };
  }

  const form = new FormData();
  form.append("what_happened", input.whatHappened);
  form.append("expected", input.expected);
  form.append("source", input.source);
  form.append("page", input.page);
  form.append("user_agent", input.userAgent);
  form.append("browser", input.browser);
  form.append("app_env", input.appEnv);
  form.append("api_base_url", input.apiHost);
  form.append("client_time", input.clientTime);
  form.append(
    "request_info",
    input.failedRequest
      ? JSON.stringify({
          url: input.failedRequest.url,
          method: input.failedRequest.method,
          status: input.failedRequest.status ?? null,
          code: input.failedRequest.code ?? null,
        })
      : "",
  );
  form.append("ai_session_id", input.aiSessionId);
  if (input.screenshot) {
    form.append("screenshot", input.screenshot);
  }

  try {
    // `Content-Type: undefined` CLEARS it for this request so the browser can set
    // `multipart/form-data` with the boundary it generates. Simply omitting the header is
    // not enough: the shared client sets `application/json` on `defaults.headers.common`,
    // which applies to every request including this one. With a JSON content type on a
    // multipart body the server parses no form fields at all and rejects the request as
    // though the client sent nothing, rather than as a header problem. Setting the header to
    // `"multipart/form-data"` by hand does NOT work either: it omits the boundary, which
    // only the browser can generate. Same reasoning as `createMigration` in migrationsApi.
    const resp = await axios.post("/feedback", form, {
      headers: { "Content-Type": undefined },
      skipGlobalErrorHandler: true,
    });
    const data: FeedbackResponseDto = resp.data ?? {};
    return { outcome: "sent", guid: data.guid ?? null };
  } catch (err) {
    console.error(err);
    if (statusOf(err) === RATE_LIMIT_STATUS) {
      return { outcome: "rateLimited" };
    }
    return { outcome: "failed" };
  }
}
